<template>
  <div v-if="banner" class="sponsor-banner" @click="handleClick" :style="bannerStyle" :class="{ 'has-bg-image': hasBgImage }">
    <div class="banner-content">
      <!-- Show logo if there's an image and it's NOT being used as a background -->
      <img
        v-if="banner.imageUrl && !banner.useImageAsBackground"
        :src="banner.imageUrl"
        :alt="banner.title"
        class="banner-image"
        :style="{ objectFit: banner.imageFit || 'contain' }"
      />
      <!-- If NO logo AND NO custom background, we can show a placeholder megaphone ONLY IF there's no custom content either -->
      <div v-else-if="!banner.imageUrl && !hasCustomContent" class="banner-placeholder">
        <i class="pi pi-megaphone"></i>
      </div>

      <!-- Custom HTML Content takes up the rest of the space if available -->
      <div
        v-if="hasCustomContent"
        class="custom-content ql-editor"
        :style="{ color: hasBgImage ? '#ffffff' : 'inherit' }"
      >
        <MarkdownRenderer 
          :content="banner.content || ''" 
          :format="banner.contentFormat || 'html'" 
        />
      </div>
      
      <!-- Default Text if no Custom HTML Content -->
      <div v-else class="banner-text">
        <span class="sponsor-label" :style="{ color: hasBgImage ? '#ffffff' : 'var(--color-accent)' }">Sponsored</span>
        <span class="banner-title" :style="{ color: hasBgImage ? '#ffffff' : 'var(--color-text-primary)' }">{{ banner.title }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import MarkdownRenderer from "@/components/ui/MarkdownRenderer.vue";

interface SponsorBanner {
  id: string;
  title: string;
  url: string;
  imageUrl?: string;
  content?: string;
  contentFormat?: 'html' | 'markdown';
  backgroundColor?: string;
  useImageAsBackground?: boolean;
  imageFit?: 'contain' | 'cover' | 'fill' | 'none';
}

interface Props {
  banner?: SponsorBanner;
}

const props = defineProps<Props>();

const hasCustomContent = computed(() => {
  const content = props.banner?.content?.trim();
  if (!content) return false;
  
  // PrimeVue Editor/Quill often sets empty values to these
  const emptyValues = ['<p><br></p>', '<p></p>', '<div></div>', '<div><br></div>'];
  return !emptyValues.includes(content);
});

const hasBgImage = computed(() => {
  return !!(props.banner?.useImageAsBackground && props.banner?.imageUrl);
});

const bannerStyle = computed(() => {
  if (!props.banner) return {};
  const style: Record<string, string> = {};
  
  if (hasBgImage.value) {
    style.backgroundImage = `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${props.banner?.imageUrl})`;
    style.backgroundSize = props.banner.imageFit || 'cover';
    style.backgroundPosition = 'center';
    style.backgroundRepeat = 'no-repeat';
    style.color = '#ffffff';
    style.border = 'none';
  } else if (props.banner.backgroundColor && props.banner.backgroundColor !== '#ffffff') {
    style.background = props.banner.backgroundColor;
    style.borderColor = 'transparent';
  }
  
  return style;
});

function handleClick() {
  if (props.banner?.url) {
    window.open(props.banner.url, "_blank", "noopener");
  }
}
</script>

<style scoped>
.sponsor-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px var(--spacing-sm);
  background: linear-gradient(
    135deg,
    var(--color-bg-secondary),
    var(--color-accent-subtle)
  );
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.sponsor-banner:hover {
  border-color: var(--color-accent);
  transform: translateY(-1px);
}

.sponsor-banner.has-bg-image:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex: 1;
  min-width: 0; /* Important for text truncation/wrapping in flex */
  z-index: 1;
}

.banner-image {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-md);
  object-fit: cover;
}

.banner-placeholder {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-md);
  background: var(--color-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sponsor-label {
  font-size: var(--font-size-xs);
  color: var(--color-accent);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.banner-title {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.banner-close {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-full);
  background: transparent;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.banner-close:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
}

.custom-content {
  width: 100%; /* Force it to respect parent width */
  min-width: 0;
  white-space: normal; /* Ensure text wraps */
  overflow-wrap: break-word; /* Break long words only if necessary */
  word-break: normal; /* Do NOT break words in the middle otherwise */
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  /* Ensure images inside don't break layout */
  :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: var(--radius-sm);
  }
  /* Normalize paragraphs */
  :deep(p) {
    margin: 0;
    line-height: 1.4;
  }
}

.custom-content.ql-editor {
  padding: 0;
  height: auto;
  overflow-y: hidden;
}

/* Quill Alignment Classes (Flattened for safety) */
.custom-content :deep(.ql-align-center) {
  text-align: center;
}
.custom-content :deep(.ql-align-right) {
  text-align: right;
}
.custom-content :deep(.ql-align-justify) {
  text-align: justify;
}
</style>
